import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { LoginRequest, LoginResponse, User, JwtPayload, SendOtpRequest, SendOtpResponse, SignupRequest, SignupResponse } from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'jwt_token';
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;

  constructor(private http: HttpClient) {
    const token = this.getToken();
    const user = token ? this.parseUserFromToken(token) : null;
    this.currentUserSubject = new BehaviorSubject<User | null>(user);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  sendOtp(request: SendOtpRequest): Observable<SendOtpResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    return this.http.post<SendOtpResponse>(
      `${environment.apiUrl}/User/send-otp`,
      request,
      { headers }
    );
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/User/login`,
      credentials,
      { headers }
    ).pipe(
      tap(response => {
        if (response.token) {
          this.setToken(response.token);
          const user = this.parseUserFromToken(response.token);
          this.currentUserSubject.next(user);
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token || token.length === 0) {
      return false;
    }
    
    // Check if token is expired
    try {
      const payload = this.decodeJwt(token);
      if (payload.exp) {
        const expirationDate = new Date(payload.exp * 1000);
        if (expirationDate <= new Date()) {
          this.logout();
          return false;
        }
      }
      return true;
    } catch {
      return false;
    }
  }

  private parseUserFromToken(token: string): User | null {
    try {
      const payload = this.decodeJwt(token);
      
      return {
        id: payload.sub || payload.id || payload.nameid || '',
        firstName: payload.given_name || '',
        lastName: payload.family_name || '',
        email: payload.email || payload.unique_name || '',
        role: payload.role || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || ''
      };
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  }

  private decodeJwt(token: string): JwtPayload {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return {};
    }
  }

  signup(request: SignupRequest): Observable<SignupResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    return this.http.post<SignupResponse>(
      `${environment.apiUrl}/user/signup`,
      request,
      { headers }
    );
  }
}
